import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { supabase } from '../../utils/supabaseClient';
import { computeAiDataReadiness } from '../../utils/aiDataReadiness';
import { useSupplierLocations, countryOptionsWith, OTHER_COUNTRY } from '../../utils/useSupplierLocations';
import { PRODUCT_CERTS as PRODUCT_IMPACTS, NO_FILE_CERTS, productCertLabel } from '../../constants/productCerts';
import { useConfirm } from '../ConfirmDialog';
import './ProductModal.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const FALLBACK_IMAGE = 'https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/logo_with_text_black.png';

// Matches the server's LOOSE_PACKED_RE in services/pcfInputs.js.
const LOOSE_PACKED_VALUE_RE = /^loose\s*pack(ed)?\.?\s*$/i;

function hasViewableResults(product) {
  const step = Number(product?.lca?.current_step || 0);
  const total = product?.lca?.total_emissions_kg;
  return step >= 5 && total !== null && total !== undefined;
}

function formatWeight(grams) {
  if (grams == null) return null;
  const g = parseFloat(grams);
  if (isNaN(g)) return null;
  return g >= 1000 ? `${(g / 1000).toFixed(2)} kg` : `${g} g`;
}

export default function ProductModal({
  mode = 'create',        // 'create' | 'view'
  show,
  onClose,
  onSuccess,
  product = null,
  supplierInfo,
  getToken,
  promoCategories = [],
  usesPromoTaxonomy = true,
  supplierCategoryOptions = [],
  canEditProducts = false,
  assessmentLimitReached = false,
  isFreePlan = false,
  onAiAssess,
  onUnlink,
  unlinking = false,
  // True when the modal was opened because an auto-estimate couldn't run for
  // lack of required data — shows a prominent prompt pointing at the fix.
  highlightMissingData = false,
}) {
  const isView = mode === 'view';
  const isCreate = mode === 'create';
  const navigate = useNavigate();
  const { confirm, alert } = useConfirm();
  const supplierCountries = useSupplierLocations();

  const emptyMaterialRow = { category: '', subcategory: '', percent: '' };
  const emptyPackagingRow = { type: '', weight_g: '' };

  const emptyForm = {
    name: '',
    code: '',
    description: '',
    weight_grams: '',
    promo_category_id: '',
    promo_subcategory_id: '',
    supplier_category: '',
    supplier_subcategory: '',
    country_of_origin: '',
    supplier_location: '',
    material_rows: [{ ...emptyMaterialRow }],
    loose_packed: false,
    packaging_rows: [{ ...emptyPackagingRow }],
  };

  const [formData, setFormData] = useState(emptyForm);
  const [packagingOptions, setPackagingOptions] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);
  const [materialSubcatsCache, setMaterialSubcatsCache] = useState({});
  // Free-text materials from imports/legacy products — shown as a hint when
  // there are no structured rows yet, and preserved on save.
  const [legacyMaterialsText, setLegacyMaterialsText] = useState('');
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState(0);

  // Certifications state
  const [assessmentId, setAssessmentId] = useState(null);
  const [selectedCerts, setSelectedCerts] = useState([]);
  const [certFiles, setCertFiles] = useState({});
  const [certOther, setCertOther] = useState('');
  const [uploadingCert, setUploadingCert] = useState(null);

  // Auto-estimate state
  const [autoEstimating, setAutoEstimating] = useState(false);
  const [autoEstimateError, setAutoEstimateError] = useState('');
  const [autoEstimateNotice, setAutoEstimateNotice] = useState('');
  // Shows the "add a weight to assess" prompt — set when the modal is opened
  // for a blocked assessment, or when the user clicks Auto-estimate while the
  // required data is still missing. Clears once the data is filled in.
  const [showWeightPrompt, setShowWeightPrompt] = useState(false);
  const weightInputRef = useRef(null);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  // Activate the missing-data prompt when the modal is opened for a blocked
  // assessment; clear it whenever the modal closes.
  useEffect(() => {
    if (show && highlightMissingData) setShowWeightPrompt(true);
    if (!show) setShowWeightPrompt(false);
  }, [show, highlightMissingData]);

  // Scroll to and focus the weight field — the fix for a blocked assessment.
  const focusWeightField = () => {
    const el = weightInputRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  };

  // Load the canonical dropdown options the LCA wizard uses — packaging
  // materials (Step 3) and material categories (Step 2) — so selections here
  // feed the AI estimate directly instead of free text.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/packaging-materials`);
        if (!cancelled) setPackagingOptions(Array.isArray(data?.data) ? data.data : []);
      } catch {
        if (!cancelled) setPackagingOptions([]);
      }
    })();
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/materials/categories`);
        if (!cancelled) {
          setMaterialCategories((data?.data || []).map((d) => d.category).filter(Boolean));
        }
      } catch {
        if (!cancelled) setMaterialCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, [show]);

  // Lazily load (and cache) the subcategory options for a material category.
  const loadMaterialSubcats = async (category) => {
    if (!category || materialSubcatsCache[category]) return;
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/materials/categories/${encodeURIComponent(category)}/subcategories`
      );
      const subs = (data?.data || []).map((d) => d.subcategory).filter(Boolean);
      setMaterialSubcatsCache((p) => ({ ...p, [category]: subs }));
    } catch {
      setMaterialSubcatsCache((p) => ({ ...p, [category]: [] }));
    }
  };

  // Populate form when in view mode. Fields the supplier never filled in fall
  // back to pcf_inputs — values mined/AI-extracted from the imported source
  // data — so the form shows everything we know; saving promotes them to the
  // real product columns.
  useEffect(() => {
    if (show && isView && product) {
      const pcf = product.pcf_inputs || {};

      // Structured material rows (modal-entered) come back via pcf_inputs;
      // older products only have free text, kept as a hint.
      const materialRows = (Array.isArray(pcf.materials) ? pcf.materials : [])
        .filter((m) => m && m.category)
        .map((m) => ({
          category: m.category,
          subcategory: m.subcategory || '',
          percent: m.percent != null ? String(m.percent) : '',
        }));
      setLegacyMaterialsText(
        materialRows.length === 0
          ? (product.source_metadata?.material || pcf.materials_text || '')
          : ''
      );
      materialRows.forEach((r) => loadMaterialSubcats(r.category));

      const loosePacked = !!pcf.no_packaging || LOOSE_PACKED_VALUE_RE.test(product.packaging || '');
      let packagingRows = (Array.isArray(pcf.packaging) ? pcf.packaging : [])
        .filter((p) => p && p.type)
        .map((p) => ({ type: p.type, weight_g: p.weight_g > 0 ? String(p.weight_g) : '' }));
      if (!loosePacked && packagingRows.length === 0 && product.packaging) {
        packagingRows = [{
          type: product.packaging,
          weight_g: product.packaging_weight_grams != null ? String(product.packaging_weight_grams) : '',
        }];
      }

      setFormData({
        name: product.name || '',
        code: product.code || '',
        description: product.description || '',
        weight_grams: product.weight_grams != null
          ? String(product.weight_grams)
          : (pcf.weight_g != null ? String(pcf.weight_g) : ''),
        promo_category_id: product.promo_category_id || '',
        promo_subcategory_id: product.promo_subcategory_id || '',
        supplier_category: product.supplier_category || '',
        supplier_subcategory: product.supplier_subcategory || '',
        country_of_origin: product.country_of_origin || pcf.origin_country || '',
        supplier_location: product.source_metadata?.origin_supplier_location || pcf.origin_address || '',
        material_rows: materialRows.length > 0 ? materialRows : [{ ...emptyMaterialRow }],
        loose_packed: loosePacked,
        packaging_rows: packagingRows.length > 0 ? packagingRows : [{ ...emptyPackagingRow }],
      });
      setUploadedImageUrls(Array.isArray(product.images) ? product.images : []);
      setActiveImage(0);
      setImageUrlInput('');
      setImageUploadError(null);
      setError('');
    } else if (show && isCreate) {
      setFormData(emptyForm);
      setLegacyMaterialsText('');
      setUploadedImageUrls([]);
      setActiveImage(0);
      setImageUrlInput('');
      setImageUploadError(null);
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, mode, product?.id]);

  // Fetch certifications when opening in view mode with a completed assessment
  useEffect(() => {
    if (!show || !isView || !product?.id || !supplierInfo?.supplier_id) {
      setAssessmentId(null);
      setSelectedCerts([]);
      setCertFiles({});
      setCertOther('');
      return;
    }
    if (!hasViewableResults(product)) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/lca/assessments`, {
          params: { supplier_id: supplierInfo.supplier_id, product_id: product.id },
        });
        if (cancelled) return;
        const asmnt = data?.data;
        if (!asmnt?.id) return;
        setAssessmentId(asmnt.id);

        const si = typeof asmnt.social_impact === 'string'
          ? JSON.parse(asmnt.social_impact)
          : asmnt.social_impact;
        if (si) {
          setSelectedCerts(si.product || []);
          setCertFiles(si.certFiles || {});
          setCertOther(si.productOther || '');
        }
      } catch (err) {
        console.warn('Could not load certifications:', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, mode, product?.id]);

  const handleCertFileUpload = async (certId, e) => {
    const file = e.target.files?.[0];
    if (!file || !supplierInfo?.supplier_id) return;
    if (file.size > 10 * 1024 * 1024) {
      alert({ title: 'File too large', message: 'Please upload a file under 10MB.' });
      e.target.value = '';
      return;
    }
    setUploadingCert(certId);
    try {
      const timestamp = Date.now();
      const ext = file.name.split('.').pop();
      const filePath = `${supplierInfo.supplier_id}/social-impact-${certId}-${timestamp}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('lca-documents')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      // No public URL: lca-documents is private. The server regenerates `url`
      // from `path` as a signed /api/cert-files link on every read, so storing
      // only the path keeps this to the documented { name, size, path } shape.
      setCertFiles((prev) => ({
        ...prev,
        [certId]: { name: file.name, size: file.size, path: filePath },
      }));
      // Auto-select the cert if not already selected
      setSelectedCerts((prev) => prev.includes(certId) ? prev : [...prev, certId]);
    } catch (err) {
      console.error('Cert upload failed:', err);
    } finally {
      setUploadingCert(null);
    }
  };

  // Every selected cert is saved and shows on the results page, with or
  // without its proof file — a file only makes the badge link to it.
  const certPayload = (selected, files) => {
    const product = selected.filter((id) => id !== 'other' || certOther.trim() !== '');
    return { product, productOther: product.includes('other') ? certOther : '', certFiles: files };
  };

  const removeCertFile = async (certId) => {
    const file = certFiles[certId];
    if (file?.path) {
      try {
        await supabase.storage.from('lca-documents').remove([file.path]);
      } catch (err) {
        console.warn('Failed to remove cert file from storage:', err);
      }
    }
    const nextCertFiles = { ...certFiles };
    delete nextCertFiles[certId];
    const nextSelected = selectedCerts.filter((x) => x !== certId);
    setCertFiles(nextCertFiles);
    setSelectedCerts(nextSelected);

    // Persist immediately so DB and storage can't drift if user navigates away.
    if (assessmentId && supplierInfo?.supplier_id) {
      try {
        const token = await getToken();
        await axios.post(
          `${API_BASE_URL}/lca/assessments/${assessmentId}/social-impact`,
          {
            supplier_id: supplierInfo.supplier_id,
            social_impact: certPayload(nextSelected, nextCertFiles),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error('Failed to persist cert removal:', err);
      }
    }
  };

  const saveCertifications = async () => {
    if (!assessmentId || !supplierInfo?.supplier_id) return;
    try {
      const token = await getToken();
      const socialData = certPayload(selectedCerts, certFiles);
      await axios.post(
        `${API_BASE_URL}/lca/assessments/${assessmentId}/social-impact`,
        { supplier_id: supplierInfo.supplier_id, social_impact: socialData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Failed to save certifications:', err);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) {
      setImageUploadError('File size must be under 1MB');
      return;
    }
    const supplierId = supplierInfo?.supplier_id;
    if (!supplierId) {
      setImageUploadError('Supplier information not available');
      return;
    }
    setUploadingImage(true);
    setImageUploadError(null);
    try {
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const filePath = `${supplierId}/product-image-${timestamp}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);
      setUploadedImageUrls((prev) => [...prev, urlData.publicUrl]);
    } catch (err) {
      setImageUploadError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // For distributors viewing a linked product: forking creates an owned copy
  // they can assess or edit (the linked row belongs to the supplier, so it
  // can't be updated in place). Returns the new product object, or null if the
  // user cancelled or the fork failed. `confirmOptions` and `onError` let the
  // caller tailor the prompt and route the failure to the right banner.
  const forkLinkedIfNeeded = async ({ confirmOptions, onError } = {}) => {
    if (!isLinkedFromSupplier) return product;
    const supplierName = product?.source_supplier?.name || 'the supplier';
    const ok = await confirm(confirmOptions || {
      title: 'Create your own copy?',
      message: `This product is linked from ${supplierName}. Running your own assessment will create your own copy of it — if ${supplierName} later publishes their own PCF, you'll get the chance to update your assessment with their version.`,
      confirmLabel: 'Create my copy',
    });
    if (!ok) return null;
    try {
      const token = await getToken();
      const res = await axios.post(
        `${API_BASE_URL}/distributor/links/${product.link_id}/fork`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return res.data?.product || null;
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to copy product into your catalogue.';
      (onError || setAutoEstimateError)(msg);
      return null;
    }
  };

  // ── Material / packaging row helpers ──────────────────────────────────────
  const updateMaterialRow = (index, field, value) => {
    setFormData((prev) => {
      const rows = prev.material_rows.map((r, i) => {
        if (i !== index) return r;
        const next = { ...r, [field]: value };
        if (field === 'category') next.subcategory = '';
        return next;
      });
      return { ...prev, material_rows: rows };
    });
    if (field === 'category' && value) loadMaterialSubcats(value);
  };

  const addMaterialRow = () => {
    setFormData((prev) => ({ ...prev, material_rows: [...prev.material_rows, { ...emptyMaterialRow }] }));
  };

  const removeMaterialRow = (index) => {
    setFormData((prev) => {
      const rows = prev.material_rows.filter((_, i) => i !== index);
      return { ...prev, material_rows: rows.length > 0 ? rows : [{ ...emptyMaterialRow }] };
    });
  };

  const updatePackagingRow = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      packaging_rows: prev.packaging_rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    }));
  };

  const addPackagingRow = () => {
    setFormData((prev) => ({ ...prev, packaging_rows: [...prev.packaging_rows, { ...emptyPackagingRow }] }));
  };

  const removePackagingRow = (index) => {
    setFormData((prev) => {
      const rows = prev.packaging_rows.filter((_, i) => i !== index);
      return { ...prev, packaging_rows: rows.length > 0 ? rows : [{ ...emptyPackagingRow }] };
    });
  };

  const filledMaterialRows = formData.material_rows.filter((r) => r.category && r.subcategory);
  const filledPackagingRows = formData.loose_packed
    ? []
    : formData.packaging_rows.filter((r) => r.type);
  const materialPercentTotal = formData.material_rows.reduce(
    (sum, r) => sum + (r.category && r.percent !== '' ? Number(r.percent) || 0 : 0), 0
  );

  // Build the product create/update payload from the current form state.
  // Shared by handleSubmit and handleAutoEstimate (which saves edits before
  // estimating so the server's fresh DB read sees them).
  // Category editor switches on the org's vertical: promo orgs pick from the
  // shared promo taxonomy (UUID ids); non-promo orgs (e.g. Bunzl) type/pick
  // their own free-text supplier_category / supplier_subcategory values.
  const renderCategoryField = (prefix) => {
    if (usesPromoTaxonomy) {
      return (
        <select
          className="form-select"
          id={`${prefix}-category`}
          value={formData.promo_category_id}
          onChange={(e) => setFormData({ ...formData, promo_category_id: e.target.value, promo_subcategory_id: '' })}
        >
          <option value="">Select a category</option>
          {promoCategories.filter(c => !c.is_other).map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      );
    }
    return (
      <>
        <input
          list={`${prefix}-cat-options`}
          className="form-control"
          id={`${prefix}-category`}
          placeholder="Type or pick a category"
          value={formData.supplier_category}
          onChange={(e) => setFormData({ ...formData, supplier_category: e.target.value })}
        />
        <datalist id={`${prefix}-cat-options`}>
          {supplierCategoryOptions.map((c) => <option key={c} value={c} />)}
        </datalist>
      </>
    );
  };

  const renderSubcategoryField = (prefix) => {
    if (usesPromoTaxonomy) {
      if (!formData.promo_category_id) return null;
      const subs = promoCategories.find(c => c.id === formData.promo_category_id)?.subcategories || [];
      if (!subs.length) return null;
      return (
        <div className="mb-3">
          <label htmlFor={`${prefix}-subcategory`} className="form-label">Subcategory</label>
          <select
            className="form-select"
            id={`${prefix}-subcategory`}
            value={formData.promo_subcategory_id}
            onChange={(e) => setFormData({ ...formData, promo_subcategory_id: e.target.value })}
          >
            <option value="">Select a subcategory</option>
            {subs.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      );
    }
    return (
      <div className="mb-3">
        <label htmlFor={`${prefix}-subcategory`} className="form-label">Subcategory</label>
        <input
          className="form-control"
          id={`${prefix}-subcategory`}
          placeholder="Optional"
          value={formData.supplier_subcategory}
          onChange={(e) => setFormData({ ...formData, supplier_subcategory: e.target.value })}
        />
      </div>
    );
  };

  const buildProductPayload = () => {
    const pendingUrl = imageUrlInput.trim();
    const allImageUrls = pendingUrl ? [...uploadedImageUrls, pendingUrl] : uploadedImageUrls;
    return {
      name: formData.name,
      code: formData.code || null,
      description: formData.description || null,
      weight_grams: formData.weight_grams ? parseFloat(formData.weight_grams) : null,
      promo_category_id: formData.promo_category_id || null,
      promo_subcategory_id: formData.promo_subcategory_id || null,
      supplier_category: (formData.supplier_category || '').trim() || null,
      supplier_subcategory: (formData.supplier_subcategory || '').trim() || null,
      country_of_origin: formData.country_of_origin || null,
      supplier_location: formData.supplier_location.trim() || null,
      images: allImageUrls,
      // Legacy free text is preserved when no structured rows are entered.
      materials: legacyMaterialsText || null,
      materials_structured: filledMaterialRows.map((r) => ({
        category: r.category,
        subcategory: r.subcategory,
        percent: r.percent !== '' ? parseFloat(r.percent) : null,
      })),
      packaging: formData.loose_packed ? 'Loose Packed' : (filledPackagingRows[0]?.type || null),
      packaging_weight_grams: filledPackagingRows[0]?.weight_g !== '' && filledPackagingRows[0]?.weight_g != null
        ? parseFloat(filledPackagingRows[0].weight_g)
        : null,
      packaging_items: filledPackagingRows.map((r) => ({
        type: r.type,
        weight_g: r.weight_g !== '' ? parseFloat(r.weight_g) : null,
      })),
    };
  };

  const handleAutoEstimate = async () => {
    if (!product?.id) return;
    // Required data still missing — don't fire a request that will fail; show
    // the prompt and take the user straight to the field instead.
    if (aiBlocked) {
      setShowWeightPrompt(true);
      setAutoEstimateNotice('');
      setAutoEstimateError('');
      focusWeightField();
      return;
    }
    const hasExisting = Number(product?.lca?.current_step || 0) >= 1;
    if (hasExisting) {
      const ok = await confirm({
        title: 'Replace the existing assessment?',
        message: 'A PCF agent will generate a new estimate from the data provided and replace this product\'s current assessment.',
        confirmLabel: 'Replace assessment',
      });
      if (!ok) return;
    }
    // Advisory data is thin (weight, the hard gate, already disables the button) —
    // warn that the estimate will be less accurate, but let the user proceed.
    if (recommendedForAi.length > 0) {
      const ok = await confirm({
        title: 'Some data is missing',
        message: `This product has no ${recommendedForAi.join(', ')}, so the AI estimate will be less accurate. You can add the missing details first, or run it anyway.`,
        confirmLabel: 'Run it anyway',
        cancelLabel: 'Add the details',
      });
      if (!ok) return;
    }
    setAutoEstimating(true);
    setAutoEstimateError('');
    setAutoEstimateNotice('');
    try {
      const token = await getToken();
      if (!token) { setAutoEstimateError('You must be logged in.'); return; }

      // If this is a linked product, fork first so we operate on a product
      // the distributor actually owns.
      const target = await forkLinkedIfNeeded();
      if (!target) return; // user cancelled or fork failed

      // Persist any unsaved edits (e.g. Country of Origin) before estimating.
      // The server re-fetches the product from the DB, so unsaved modal changes
      // would otherwise be ignored — leaving the AI without the origin it needs.
      // Skip for freshly forked copies, which already reflect the source data.
      let estimateTarget = target;
      if (!isLinkedFromSupplier && canEditProducts) {
        try {
          const saved = await axios.put(
            `${API_BASE_URL}/products/${target.id}`,
            buildProductPayload(),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (saved.data?.success && saved.data.data) {
            estimateTarget = saved.data.data;
          }
        } catch (err) {
          setAutoEstimateError(err.response?.data?.error || 'Failed to save product changes before estimating.');
          return;
        }
      }

      const response = await axios.post(
        `${API_BASE_URL}/lca/auto-estimate`,
        { product_ids: [estimateTarget.id], overwrite: hasExisting },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = response.data?.results?.[0];
      if (!result) { setAutoEstimateError('Unexpected response from server.'); return; }
      if (result.status === 'estimated') {
        // Redirect to the calculator with the product preserved in router state —
        // LCACalculator reads it from location.state and has no API fallback.
        onClose && onClose();
        navigate(`/lca-calculator/${estimateTarget.id}`, { state: { product: estimateTarget } });
        return;
      }
      if (result.status === 'skipped_exists') {
        setAutoEstimateNotice('An assessment already exists for this product.');
      } else if (result.status === 'needs_input') {
        setShowWeightPrompt(true);
        focusWeightField();
      } else {
        setAutoEstimateError(result.reason || 'Auto-estimate failed.');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setAutoEstimateError('AI auto-estimate requires a paid plan.');
      } else {
        setAutoEstimateError(data?.error || 'Auto-estimate failed. Please try again.');
      }
    } finally {
      setAutoEstimating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const token = await getToken();
      if (!token) { setError('You must be logged in'); return; }

      const payload = buildProductPayload();

      let response;
      if (isCreate) {
        response = await axios.post(`${API_BASE_URL}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        // A linked product is the supplier's row — updating it in place 404s.
        // Fork into an owned copy first, then save the edits onto that copy
        // (mirrors the auto-estimate flow). No-op for products we already own.
        const supplierName = product?.source_supplier?.name || 'the supplier';
        const target = await forkLinkedIfNeeded({
          confirmOptions: {
            title: 'Save as your own copy?',
            message: `This product is linked from ${supplierName}. Saving your changes adds your own copy to your catalogue, where you can edit it and run your own assessment.`,
            confirmLabel: 'Save my copy',
          },
          onError: setError,
        });
        if (!target) return; // user cancelled or fork failed (finally resets submitting)

        response = await axios.put(`${API_BASE_URL}/products/${target.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (response.data.success) {
        // Save certifications alongside product if we have an assessment
        if (assessmentId) {
          await saveCertifications();
        }
        onSuccess(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${isCreate ? 'create' : 'update'} product`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const images = isView
    ? (Array.isArray(product?.images) ? product.images : [])
    : uploadedImageUrls;

  const shouldShowUpgrade = isView && !hasViewableResults(product) && assessmentLimitReached;
  const viewable = isView && hasViewableResults(product);

  // Data the AI estimator needs, via the shared rules in computeAiDataReadiness
  // (the same helper the product-list Data badge uses, so the two never
  // disagree). Resolved from the live form — already prefilled from pcf_inputs —
  // so it updates as the user fills fields in. Only weight is a hard gate;
  // materials / origin / packaging are advisory but still count as "missing".
  const { requiredMissing: requiredForAi, recommendedMissing: recommendedForAi, blocked: aiBlocked } =
    computeAiDataReadiness({
      weight: formData.weight_grams,
      hasMaterials: filledMaterialRows.length > 0 || !!legacyMaterialsText,
      hasOrigin: !!formData.country_of_origin || !!formData.supplier_location,
      hasPackaging: formData.loose_packed || filledPackagingRows.length > 0,
    });
  const calculable = isView && !hasViewableResults(product) && !assessmentLimitReached;
  const isPendingApproval = !!product?.lca?.is_ai_estimated && Number(product?.lca?.current_step || 0) > 0 && Number(product?.lca?.current_step || 0) < 5;
  const isLinkedFromSupplier = product?.link_status === 'linked' && !!product?.link_id;

  // ── Shared form sections (used by both create and edit layouts) ────────────

  // Materials: rows of category/type from the emission-factor taxonomy (the
  // same lists LCA Step 2 uses) plus a share of the product weight.
  const renderMaterialsEditor = () => {
    const productWeight = parseFloat(formData.weight_grams);
    return (
      <div className="mb-3">
        <label className="form-label">Materials</label>
        {legacyMaterialsText && filledMaterialRows.length === 0 && (
          <div className="form-text mt-0 mb-2">
            Imported description: “{legacyMaterialsText}” — pick the matching materials below to improve assessment accuracy.
          </div>
        )}
        {formData.material_rows.map((row, i) => {
          const subcats = materialSubcatsCache[row.category] || [];
          const pct = Number(row.percent);
          const grams = Number.isFinite(productWeight) && productWeight > 0 && row.percent !== '' && pct > 0
            ? Math.round(productWeight * pct / 10) / 10
            : null;
          return (
            <div key={i} className="row g-2 mb-2">
              <div className="col-4">
                <select
                  className="form-select"
                  aria-label="Material category"
                  value={row.category}
                  onChange={(e) => updateMaterialRow(i, 'category', e.target.value)}
                >
                  <option value="">Material…</option>
                  {row.category && !materialCategories.includes(row.category) && (
                    <option value={row.category}>{row.category}</option>
                  )}
                  {materialCategories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="col-4">
                <select
                  className="form-select"
                  aria-label="Material type"
                  value={row.subcategory}
                  disabled={!row.category}
                  onChange={(e) => updateMaterialRow(i, 'subcategory', e.target.value)}
                >
                  <option value="">Type…</option>
                  {row.subcategory && !subcats.includes(row.subcategory) && (
                    <option value={row.subcategory}>{row.subcategory}</option>
                  )}
                  {subcats.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="col-3">
                <div className="input-group">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="any"
                    className="form-control"
                    placeholder="Weight"
                    aria-label="Percent of product weight"
                    value={row.percent}
                    onChange={(e) => updateMaterialRow(i, 'percent', e.target.value)}
                  />
                  <span className="input-group-text">%</span>
                </div>
                {grams != null && <div className="form-text">≈ {grams}g</div>}
              </div>
              <div className="col-1 d-flex align-items-center justify-content-center">
                <button type="button" className="btn-close" onClick={() => removeMaterialRow(i)} aria-label="Remove material" />
              </div>
            </div>
          );
        })}
        <div className="d-flex align-items-center gap-3">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={addMaterialRow}>
            + Add material
          </button>
          {materialPercentTotal > 0 && (
            <span className={`small ${Math.abs(materialPercentTotal - 100) <= 5 ? 'text-muted' : 'text-warning-emphasis'}`}>
              Total: {Math.round(materialPercentTotal * 100) / 100}% of product weight
            </span>
          )}
        </div>
        <div className="form-text">
          Weight is each material's share of the product weight. Together they should add up to about 100%.
        </div>
      </div>
    );
  };

  // Packaging: loose-packed toggle, or one row per packaging item (the
  // canonical materials LCA Step 3 uses) with its own weight.
  const renderPackagingEditor = (idPrefix) => (
    <div className="mb-3">
      <label className="form-label">Packaging</label>
      <div className="form-check mb-2">
        <input
          className="form-check-input"
          type="checkbox"
          id={`${idPrefix}-loose-packed`}
          checked={formData.loose_packed}
          onChange={(e) => setFormData({ ...formData, loose_packed: e.target.checked })}
        />
        <label className="form-check-label" htmlFor={`${idPrefix}-loose-packed`}>
          Loose packed (no packaging)
        </label>
      </div>
      {!formData.loose_packed && (
        <>
          {formData.packaging_rows.map((row, i) => (
            <div key={i} className="row g-2 mb-2">
              <div className="col-7">
                <select
                  className="form-select"
                  aria-label="Packaging material"
                  value={row.type}
                  onChange={(e) => updatePackagingRow(i, 'type', e.target.value)}
                >
                  <option value="">Select packaging material…</option>
                  {row.type && !packagingOptions.some((o) => o.label === row.type) && (
                    <option value={row.type}>{row.type}</option>
                  )}
                  {packagingOptions.map((opt) => (
                    <option key={opt.value} value={opt.label}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-4">
                <div className="input-group">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="form-control"
                    placeholder="Weight"
                    aria-label="Packaging weight (grams)"
                    value={row.weight_g}
                    onChange={(e) => updatePackagingRow(i, 'weight_g', e.target.value)}
                  />
                  <span className="input-group-text">g</span>
                </div>
              </div>
              <div className="col-1 d-flex align-items-center justify-content-center">
                <button type="button" className="btn-close" onClick={() => removePackagingRow(i)} aria-label="Remove packaging" />
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={addPackagingRow}>
            + Add packaging
          </button>
        </>
      )}
    </div>
  );

  // Supplier location: feeds the transport origin (port lookup) in the
  // assessment.
  const renderCountryOfOriginSelect = (idPrefix) => (
    <>
      <label htmlFor={`${idPrefix}-country`} className="form-label">Country of Origin</label>
      <select
        className="form-control"
        id={`${idPrefix}-country`}
        value={formData.country_of_origin}
        onChange={(e) => setFormData({ ...formData, country_of_origin: e.target.value })}
      >
        <option value="">Select country of origin…</option>
        {countryOptionsWith(formData.country_of_origin, supplierCountries).map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
        <option value={OTHER_COUNTRY}>Other / not listed</option>
      </select>
      <div className="form-text">
        The country where the product is made.
      </div>
    </>
  );

  const renderSupplierLocationInput = (idPrefix) => (
    <div className="mb-3">
      <label htmlFor={`${idPrefix}-supplier-location`} className="form-label">Supplier Location <span className="text-muted fw-normal">(ships from)</span></label>
      <input
        type="text"
        className="form-control"
        id={`${idPrefix}-supplier-location`}
        placeholder="e.g. Shenzhen, Guangdong, China"
        value={formData.supplier_location}
        onChange={(e) => setFormData({ ...formData, supplier_location: e.target.value })}
      />
      <div className="form-text">
        The specific address the product ships from — used as the transport origin in the carbon assessment. Leave blank to fall back to the country of origin above.
      </div>
    </div>
  );

  // ─── View mode layout ───────────────────────────────────────────────────────
  if (isView) {
    return (
      <>
        <div
          className="modal fade show product-modal"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          style={{ display: 'block' }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable product-modal__dialog--view" role="document">
            <div className="modal-content product-modal__content">

              {/* Header */}
              <div className="product-modal__header">
                <button type="button" className="btn-close product-modal__close" onClick={onClose} aria-label="Close" />
              </div>

              <div className="modal-body product-modal__body">

                {/* Blocked-assessment prompt: shown when the user tried to run an
                    auto-estimate without the data it needs. Points straight at the fix. */}
                {showWeightPrompt && aiBlocked && canEditProducts && !viewable && (
                  <div className="alert alert-warning d-flex align-items-start gap-3 mb-3" role="alert">
                    <span style={{ fontSize: '1.4rem', lineHeight: 1 }} aria-hidden="true">⚖️</span>
                    <div className="flex-grow-1">
                      <strong className="d-block">Add a product weight to run the assessment</strong>
                      <span className="small">
                        Carbon footprints are calculated from the product&apos;s weight, so we need it before we can estimate{recommendedForAi.length > 0 ? ' — adding materials, country of origin and packaging will make the estimate more accurate too' : ''}.
                      </span>
                      <div className="mt-2">
                        <button type="button" className="btn btn-warning btn-sm" onClick={focusWeightField}>
                          Add weight
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Dismiss"
                      onClick={() => setShowWeightPrompt(false)}
                    />
                  </div>
                )}

                {/* Hero: image + title */}
                <div className="product-modal__hero">
                  <div className="product-modal__image-viewer">
                    <img
                      src={images[activeImage] || FALLBACK_IMAGE}
                      alt={product?.name || 'Product'}
                      onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
                    />
                    {images.length > 1 && (
                      <div className="product-modal__image-dots">
                        {images.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            className={`product-modal__dot${i === activeImage ? ' active' : ''}`}
                            onClick={() => setActiveImage(i)}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="product-modal__hero-info">
                    <h4 className="product-modal__product-name">{product?.name}</h4>
                    {product?.code && (
                      <p className="product-modal__product-sku">SKU: {product.code}</p>
                    )}

                    <div className="product-modal__meta-grid">
                      {(product?.promo_category_name || product?.supplier_category) && (
                        <div className="product-modal__meta-item">
                          <span className="product-modal__meta-label">Category</span>
                          <span className="product-modal__meta-value">
                            {product.promo_category_name || product.supplier_category}
                            {product.promo_subcategory_name && ` › ${product.promo_subcategory_name}`}
                          </span>
                        </div>
                      )}
                      {(product?.weight_grams ?? product?.pcf_inputs?.weight_g) != null && (
                        <div className="product-modal__meta-item">
                          <span className="product-modal__meta-label">Weight</span>
                          <span className="product-modal__meta-value">{formatWeight(product.weight_grams ?? product.pcf_inputs.weight_g)}</span>
                        </div>
                      )}
                      {(product?.country_of_origin || product?.pcf_inputs?.origin_country) && (
                        <div className="product-modal__meta-item">
                          <span className="product-modal__meta-label">Country of Origin</span>
                          <span className="product-modal__meta-value">{product.country_of_origin || product.pcf_inputs.origin_country}</span>
                        </div>
                      )}
                      {(product?.packaging || product?.pcf_inputs?.no_packaging || product?.pcf_inputs?.packaging?.length > 0) && (
                        <div className="product-modal__meta-item">
                          <span className="product-modal__meta-label">Packaging</span>
                          <span className="product-modal__meta-value">
                            {product.packaging
                              || (product.pcf_inputs?.no_packaging
                                ? 'Loose Packed'
                                : product.pcf_inputs.packaging.map((i) => i.type).join('; '))}
                            {product.packaging_weight_grams != null && ` · ${formatWeight(product.packaging_weight_grams)}`}
                          </span>
                        </div>
                      )}
                    </div>

                    {product?.description && (
                      <p className="product-modal__description">{product.description}</p>
                    )}
                  </div>
                </div>

                {/* Assessment actions */}
                <div className="product-modal__section product-modal__assessment-section">
                  <p className="product-modal__section-label">Carbon Assessment</p>
                  <div className="product-modal__action-row">
                    {isFreePlan && !viewable && (
                      <button
                        type="button"
                        className="btn product-modal__ai-assess-btn"
                        onClick={() => onAiAssess && onAiAssess()}
                        title="See how AI can assess this product for you"
                      >
                        ✨ Auto assess
                      </button>
                    )}
                    {viewable && (
                      <>
                        <Link
                          to={`/product-results/${product.id}?supplier_id=${supplierInfo?.supplier_id}`}
                          className="btn btn-success"
                          state={{ product }}
                          onClick={onClose}
                        >
                          View Results
                        </Link>
                        <Link
                          to={`/lca-calculator/${product.id}`}
                          className="btn btn-outline-success"
                          state={{ product }}
                          onClick={onClose}
                        >
                          Recalculate / Edit
                        </Link>
                      </>
                    )}
                    {shouldShowUpgrade && (
                      <Link to="/billing" className="btn btn-upgrade-stripe" onClick={onClose} title="Upgrade to assess more SKUs">
                        Upgrade plan
                      </Link>
                    )}
                    {calculable && (
                      isLinkedFromSupplier ? (
                        <button
                          type="button"
                          className="btn btn-outline-success"
                          onClick={async () => {
                            const target = await forkLinkedIfNeeded();
                            if (!target) return;
                            onClose && onClose();
                            navigate(`/lca-calculator/${target.id}`, { state: { product: target } });
                          }}
                        >
                          Manually assess
                        </button>
                      ) : (
                        <Link
                          to={`/lca-calculator/${product.id}`}
                          className={isPendingApproval ? 'btn btn-dark' : 'btn btn-outline-success'}
                          state={{ product }}
                          onClick={onClose}
                        >
                          {isPendingApproval ? 'Review & approve' : 'Calculate footprint'}
                        </Link>
                      )
                    )}
                    {product?.lca?.is_linked && (
                      <button
                        type="button"
                        className="btn btn-outline-warning btn-sm"
                        onClick={() => onUnlink && onUnlink(product)}
                        disabled={unlinking}
                        title="Unlink this SKU from its shared assessment"
                      >
                        {unlinking ? 'Unlinking…' : 'Unlink Assessment'}
                      </button>
                    )}
                    {canEditProducts && !assessmentLimitReached && !isPendingApproval && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleAutoEstimate}
                        disabled={autoEstimating}
                        title="Use AI to match this product to a finished-product assessment and pre-fill the LCA"
                      >
                        {autoEstimating ? 'Estimating…' : 'Auto-estimate PCF'}
                      </button>
                    )}
                  </div>
                  {autoEstimateError && (
                    <div className="alert alert-danger py-2 small mt-2 mb-0">{autoEstimateError}</div>
                  )}
                  {autoEstimateNotice && (
                    <div className="alert alert-warning py-2 small mt-2 mb-0">{autoEstimateNotice}</div>
                  )}
                  {canEditProducts && !viewable && !isPendingApproval && (
                    <div className="mt-2">
                      <span className="small text-muted fw-semibold me-2">Data</span>
                      {aiBlocked ? (
                        <span
                          className="badge bg-warning-subtle text-warning-emphasis border"
                          title={`Add ${requiredForAi.join(' and ')} to enable an AI assessment`}
                        >
                          Missing: {requiredForAi.join(', ')}
                        </span>
                      ) : (
                        <span
                          className="badge bg-success-subtle text-success border"
                          title="Has the data needed for an AI assessment"
                        >
                          Ready for auto assessment
                        </span>
                      )}
                      {!aiBlocked && recommendedForAi.map((item) => (
                        <span
                          key={item}
                          className="badge bg-warning-subtle text-warning-emphasis border ms-1"
                        >
                          No {item}
                        </span>
                      ))}
                      {aiBlocked ? (
                        <p className="text-warning-emphasis small mt-2 mb-0">
                          To run an auto assessment, first add the data above (edit below, then save).
                        </p>
                      ) : recommendedForAi.length > 0 ? (
                        <p className="text-muted small mt-2 mb-0">
                          Adding the data above will make the AI estimate more accurate (edit below).
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Edit section — paid users only */}
                {canEditProducts && (
                  <form onSubmit={handleSubmit}>
                    <div className="product-modal__section">
                      <p className="product-modal__section-label">Edit Details</p>

                      {error && (
                        <div className="alert alert-danger py-2 mb-3" role="alert">{error}</div>
                      )}

                      <div className="mb-3">
                        <label htmlFor="pm-name" className="form-label">Product Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          id="pm-name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-6">
                          <label htmlFor="pm-code" className="form-label">Product Code / SKU</label>
                          <input
                            type="text"
                            className="form-control"
                            id="pm-code"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                          />
                        </div>
                        <div className="col-6">
                          <label htmlFor="pm-weight" className="form-label">
                            Weight (grams){showWeightPrompt && aiBlocked && <span className="text-warning-emphasis"> *</span>}
                          </label>
                          <input
                            ref={weightInputRef}
                            type="number"
                            className={`form-control${showWeightPrompt && aiBlocked ? ' border-warning' : ''}`}
                            id="pm-weight"
                            min="0"
                            step="0.01"
                            value={formData.weight_grams}
                            onChange={(e) => setFormData({ ...formData, weight_grams: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-6">
                          <label htmlFor="pm-category" className="form-label">Category</label>
                          {renderCategoryField('pm')}
                        </div>
                        <div className="col-6">
                          {renderCountryOfOriginSelect('pm')}
                        </div>
                      </div>

                      {renderSubcategoryField('pm')}

                      {renderSupplierLocationInput('pm')}

                      <div className="mb-3">
                        <label htmlFor="pm-description" className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          id="pm-description"
                          rows="2"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                      </div>

                      {renderMaterialsEditor()}

                      {renderPackagingEditor('pm')}

                      {/* Images */}
                      <div>
                        <label className="form-label">Images</label>
                        {uploadedImageUrls.length > 0 && (
                          <div className="product-modal__image-grid mb-2">
                            {uploadedImageUrls.map((url, i) => (
                              <div key={i} className="product-modal__image-thumb">
                                <img src={url} alt={`Product ${i + 1}`} />
                                <button
                                  type="button"
                                  className="product-modal__image-remove"
                                  onClick={() => setUploadedImageUrls((prev) => prev.filter((_, idx) => idx !== i))}
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="d-flex gap-2 mb-2">
                          <input
                            type="url"
                            className="form-control form-control-sm"
                            placeholder="Paste image URL"
                            value={imageUrlInput}
                            onChange={(e) => setImageUrlInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const url = imageUrlInput.trim();
                                if (url) { setUploadedImageUrls((p) => [...p, url]); setImageUrlInput(''); }
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                              const url = imageUrlInput.trim();
                              if (url) { setUploadedImageUrls((p) => [...p, url]); setImageUrlInput(''); }
                            }}
                            disabled={!imageUrlInput.trim()}
                          >Add</button>
                        </div>
                        <label htmlFor="pm-img-upload" className={`product-modal__upload-link${uploadingImage ? ' disabled' : ''}`}>
                          {uploadingImage ? 'Uploading…' : 'or upload a file (max 1MB)'}
                        </label>
                        <input
                          type="file"
                          id="pm-img-upload"
                          accept="image/*"
                          style={{ display: 'none' }}
                          disabled={uploadingImage}
                          onChange={(e) => {
                            if (e.target.files?.[0]) { handleImageUpload(e.target.files[0]); e.target.value = ''; }
                          }}
                        />
                        {imageUploadError && <div className="text-danger small mt-1">{imageUploadError}</div>}
                      </div>

                      {/* Certifications — dropdown add, compact list */}
                        <div className="mt-3">
                          <label className="form-label">Product Certifications</label>
                          {/* Added certs: each shows on the results page as soon as it's
                              selected; a certificate is optional but recommended. */}
                          {selectedCerts.length > 0 && (
                            <div className="product-modal__cert-list mb-2">
                              {selectedCerts.map((certId) => {
                                const label = certId === 'other' ? (certOther || 'Other') : productCertLabel(certId);
                                const file = certFiles[certId];
                                const wantsFile = !file && !NO_FILE_CERTS.includes(certId);
                                return (
                                  <div key={certId} className={`product-modal__cert-item ${wantsFile ? 'pending' : 'active'}`}>
                                    <div className="product-modal__cert-header">
                                      <span className="product-modal__cert-label">{label}</span>
                                      <div className="product-modal__cert-file-info">
                                        {file && (
                                          <span className="product-modal__cert-file-name" title={file.name}>
                                            {file.name}
                                          </span>
                                        )}
                                        {wantsFile && (uploadingCert === certId ? (
                                          <span className="product-modal__cert-hint">Uploading…</span>
                                        ) : (
                                          <label className="product-modal__cert-upload" title="A supporting document is recommended">
                                            Upload certificate
                                            <input
                                              type="file"
                                              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                                              style={{ display: 'none' }}
                                              onChange={(e) => { handleCertFileUpload(certId, e); e.target.value = ''; }}
                                            />
                                          </label>
                                        ))}
                                        <button
                                          type="button"
                                          className="product-modal__cert-remove"
                                          onClick={() => removeCertFile(certId)}
                                          title="Remove certification"
                                        >×</button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {selectedCerts.some((id) => !certFiles[id] && !NO_FILE_CERTS.includes(id)) && (
                            <div className="product-modal__cert-note">
                              Certifications without a supporting document still show on the results page, but uploading the certificate is recommended so customers can check it.
                            </div>
                          )}

                          {/* Certs are stored on the footprint, so without one there's nowhere to save them. */}
                          {!assessmentId && (
                            <div className="product-modal__cert-note">
                              Certifications are saved with the product’s footprint. You can add them once it has one.
                            </div>
                          )}

                          {/* Dropdown to add a cert — it's added straight away; the certificate can follow */}
                          {assessmentId && (() => {
                            const available = PRODUCT_IMPACTS.filter((c) => !selectedCerts.includes(c.id));
                            if (available.length === 0) return null;
                            return (
                              <div className="product-modal__cert-add-row">
                                <select
                                  className="form-select form-select-sm"
                                  value=""
                                  onChange={(e) => {
                                    const certId = e.target.value;
                                    if (certId) setSelectedCerts((prev) => (prev.includes(certId) ? prev : [...prev, certId]));
                                  }}
                                >
                                  <option value="">Add certification…</option>
                                  {available.map((c) => (
                                    <option key={c.id} value={c.id}>{c.label}</option>
                                  ))}
                                </select>
                              </div>
                            );
                          })()}
                        </div>
                    </div>

                    <div className="product-modal__footer">
                      <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Close</button>
                      <button type="submit" className="btn btn-success" disabled={submitting}>
                        {submitting ? 'Saving…' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                )}

                {!canEditProducts && (
                  <div className="product-modal__footer product-modal__footer--noborder">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" onClick={onClose} />
      </>
    );
  }

  // ─── Create mode layout ─────────────────────────────────────────────────────
  return (
    <>
      <div
        className="modal fade show product-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ display: 'block' }}
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content product-modal__content">
            <div className="modal-header product-modal__header">
              <h5 className="modal-title product-modal__title">Create New Product</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body product-modal__body">
                {error && <div className="alert alert-danger py-2 mb-3" role="alert">{error}</div>}

                <div className="product-modal__section">
                  <p className="product-modal__section-label">Basic Info</p>

                  <div className="mb-3">
                    <label htmlFor="pm-c-name" className="form-label">Product Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      id="pm-c-name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label htmlFor="pm-c-code" className="form-label">Product Code / SKU</label>
                      <input
                        type="text"
                        className="form-control"
                        id="pm-c-code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <label htmlFor="pm-c-weight" className="form-label">Weight (grams)</label>
                      <input
                        type="number"
                        className="form-control"
                        id="pm-c-weight"
                        min="0"
                        step="0.01"
                        value={formData.weight_grams}
                        onChange={(e) => setFormData({ ...formData, weight_grams: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label htmlFor="pm-c-category" className="form-label">Category</label>
                      {renderCategoryField('pm-c')}
                    </div>
                    <div className="col-6">
                      {renderCountryOfOriginSelect('pm-c')}
                    </div>
                  </div>

                  {renderSubcategoryField('pm-c')}

                  {renderSupplierLocationInput('pm-c')}

                  <div className="mb-3">
                    <label htmlFor="pm-c-description" className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      id="pm-c-description"
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>

                  {renderMaterialsEditor()}

                  {renderPackagingEditor('pm-c')}
                </div>

                <div className="product-modal__section">
                  <p className="product-modal__section-label">Images</p>

                  {uploadedImageUrls.length > 0 && (
                    <div className="product-modal__image-grid mb-3">
                      {uploadedImageUrls.map((url, i) => (
                        <div key={i} className="product-modal__image-thumb">
                          <img src={url} alt={`Product ${i + 1}`} />
                          <button
                            type="button"
                            className="product-modal__image-remove"
                            onClick={() => setUploadedImageUrls((prev) => prev.filter((_, idx) => idx !== i))}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="d-flex gap-2 mb-2">
                    <input
                      type="url"
                      className="form-control form-control-sm"
                      placeholder="Paste image URL"
                      value={imageUrlInput}
                      onChange={(e) => setImageUrlInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const url = imageUrlInput.trim();
                          if (url) { setUploadedImageUrls((p) => [...p, url]); setImageUrlInput(''); }
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        const url = imageUrlInput.trim();
                        if (url) { setUploadedImageUrls((p) => [...p, url]); setImageUrlInput(''); }
                      }}
                      disabled={!imageUrlInput.trim()}
                    >Add</button>
                  </div>

                  <label htmlFor="pm-c-img-upload" className={`product-modal__upload-link${uploadingImage ? ' disabled' : ''}`}>
                    {uploadingImage ? 'Uploading…' : 'or upload a file (max 1MB)'}
                  </label>
                  <input
                    type="file"
                    id="pm-c-img-upload"
                    accept="image/*"
                    style={{ display: 'none' }}
                    disabled={uploadingImage}
                    onChange={(e) => {
                      if (e.target.files?.[0]) { handleImageUpload(e.target.files[0]); e.target.value = ''; }
                    }}
                  />
                  {imageUploadError && <div className="text-danger small mt-1">{imageUploadError}</div>}
                </div>
              </div>

              <div className="modal-footer product-modal__footer">
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn btn-success" disabled={submitting}>
                  {submitting ? 'Creating…' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
}
